"use client";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";

import { Track } from "@/src/lib/tracking";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";
import type { MemberClient } from "@features/api/memberApi";
import { rootStore } from "@store/rootStore";
import { I18nUtil } from "@utils/i18n";
import { normalizeLocaleCode } from "@utils/i18n.locale";

/**
 * 把后端返回的语言（`/platform/owners` 的 `locale`）回写进 store + owner 缓存。
 *
 * 不写的话，下一次冷启动会先用缓存里的旧 `locale` 恢复 ownerInfo，对账逻辑会拿旧值再比一次、
 * 再补发一次同样的写请求（不会写错，但每次启动都白打一枪）。
 */
export function patchOwnerLocaleInStore(backendLocale: string) {
  const ownerInfo = rootStore.user.ownerInfo;
  if (!ownerInfo?.id) {
    return;
  }

  const nextOwnerInfo = { ...ownerInfo, locale: backendLocale };
  rootStore.user.ownerInfo = nextOwnerInfo;
  authCacheManager.saveOwnerInfoCache(nextOwnerInfo, ownerInfo.id);
}

/**
 * 登录后语言对账的判定：**要不要把本机语言补写给后端，写哪一档**。
 *
 * @param remoteLocale 后端 `/platform/owners` 的 `locale`（`en-US` / `zh-TW` / 空）
 * @param currentLocale 本机此刻正在用的 locale 段（`I18nUtil.getCurrentLocale()`：`en` / `zh-tw`）
 * @returns 要写入后端的语言码（`en-US` / `zh-TW`）；已经一致则 null（不发请求）
 *
 * 判据是**归一化后**比较而非字符串相等：后端历史值大小写不一（`en-us` / `en-US`），
 * 直接比字符串会每次进站都判不一致、每次都白写一枪。后端为空（新用户从没写过）也算不一致
 * —— 那正是本次修复的主场景。
 */
export function resolveOwnerLocaleSyncTarget(
  remoteLocale: string | null | undefined,
  currentLocale: string,
): string | null {
  if (normalizeLocaleCode(remoteLocale) === currentLocale) {
    return null;
  }
  return I18nUtil.getBackgroundLanguageKey(currentLocale);
}

/**
 * 登录后把「本机正在用的语言」对账到后端（`POST /owners/{id}/setting {locale}`）。
 *
 * 根因：语言此前**只在设置页显式切换时**才同步后端（`ProfileLanguageRow`）。新用户按设备语言进站
 * （中间件按 `Accept-Language` 定 locale 段）后从不写后端，于是后端 owner 的语言一直是注册时的默认值
 * —— 推送 / 邮件 / AI 回复语言全按错的语言下发，除非用户手动切一次语言才碰巧修正。
 *
 * 判据：后端 `locale` 归一化后与本机当前 locale 不一致（**含后端为空**）就补写一次。
 * 方向是「前端 → 后端」而不是反向：本站 UI 语言由 URL locale 段 / `locale` cookie 决定，
 * 后端语言只是内容下发口径，反向回灌会让用户看到的界面语言与刚选的设备语言打架。
 *
 * 幂等：同一 `ownerId` + 同一目标语言在本次会话只写一次（失败可重试）；写成功后回写 store/缓存，
 * 下次启动比对即相等，不再发请求。
 */
export function useSyncOwnerLocaleOnLogin(memberApi?: MemberClient<any>) {
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo, profileState } = useSnapshot(rootStore.user);
  /** 已发起过的 `${ownerId}:${目标语言}`，防止 store 回写触发的重渲染再打一次 */
  const attemptedRef = useRef<string | null>(null);

  const ownerId = ownerInfo?.id ?? "";
  const remoteLocale = ownerInfo?.locale ?? "";

  useEffect(() => {
    if (
      !memberApi ||
      !isLoggedIn ||
      !ownerId ||
      profileState !== "hasProfile"
    ) {
      return;
    }

    const targetLocale = resolveOwnerLocaleSyncTarget(
      remoteLocale,
      I18nUtil.getCurrentLocale(),
    );
    if (!targetLocale) {
      return;
    }

    const attemptKey = `${ownerId}:${targetLocale}`;
    if (attemptedRef.current === attemptKey) {
      return;
    }
    attemptedRef.current = attemptKey;

    // 只带 locale，不带 nick_name —— 否则会把用户昵称覆写掉（后端两字段同一个接口）
    memberApi.owners
      .updateOwnerSetting(ownerId, { locale: targetLocale })
      .then(() => {
        patchOwnerLocaleInStore(targetLocale);
      })
      .catch((error: unknown) => {
        // 允许下次进入（换页 / 重新登录）再试一次，语言错了比多打一次请求更糟
        attemptedRef.current = null;
        Track.log("error", "useSyncOwnerLocale", "sync owner locale failed", {
          cause: String(error),
        });
      });
  }, [memberApi, isLoggedIn, ownerId, remoteLocale, profileState]);
}
